import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

// Comprehensive fallback config matching the shape produced by the Configuration tab.
// Ensures the attendance admin page never sees undefined for critical fields.
const FALLBACK_CONFIG = {
  workStartTime: "09:00",
  workEndTime: "18:00",
  workingHoursPerDay: 8,
  workingDaysPerWeek: 5,
  weekendDays: ["Saturday", "Sunday"],
  isOvernightShift: false,
  shiftDayPivotHour: null,
  absenceDeductionPerDay: 0,
  lateDeductionRules: [
    { minutesFrom: 0, minutesTo: 15, deductionPercentage: 0 },
    { minutesFrom: 15, minutesTo: 30, deductionPercentage: 25 },
    { minutesFrom: 30, minutesTo: 60, deductionPercentage: 50 },
    { minutesFrom: 60, minutesTo: null, deductionPercentage: 100 },
  ],
  overtimeEnabled: true,
  overtimeRate: 1.5,
  overtimeStartAfter: 8,
  maxOvertimeHoursPerDay: 4,
  // FIX: was missing — caused recordConfig?.overtimeApprovalRequired to always be undefined
  overtimeApprovalRequired: false,
  defaultDailyRatePercentage: 0,
  halfDayRequestThreshold: null,
};

const useCompanyConfig = ({ BASE_URL, language, profile }) => {
  const [clientConfig, setClientConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [companyConfigs, setCompanyConfigs] = useState({});

  // ── load single-company (legacy) config ────────────────────────────────────
  const loadClientConfig = async (validateToken) => {
    try {
      setConfigLoading(true);

      if (!validateToken()) {
        console.error("❌ Cannot load config - invalid token");
        setClientConfig(FALLBACK_CONFIG);
        return;
      }

      const clientName =
        profile?.clientName || localStorage.getItem("clientName") || "Infinity";
      console.log(`🔧 Loading configuration for client: ${clientName}`);

      const response = await apiFetch(
        `${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}`,
        { method: "GET", headers: { "Accept-Language": language } }
      );
      const data = await response.json();

      if (data.success && data.data) {
        setClientConfig(data.data);
        console.log(`✅ Client config loaded:`, data.data);
        console.log(`   Work Start: ${data.data.workStartTime}`);
        console.log(`   Work End:   ${data.data.workEndTime}`);
      } else {
        console.warn("⚠️ No config found, using defaults");
        setClientConfig(FALLBACK_CONFIG);
      }
    } catch (error) {
      console.error("❌ Error loading client config:", error);
      setClientConfig(FALLBACK_CONFIG);
    } finally {
      setConfigLoading(false);
    }
  };

  // ── load all company configs (keyed by companyName) ────────────────────────
  // Shape: { [companyName]: { current: ConfigObject, history: ArchiveObject[] } }
  const loadAllCompanyConfigs = async (attendanceRecords) => {
    try {
      const clientName =
        profile?.clientName || localStorage.getItem("clientName") || "Infinity";

      const uniqueCompanies = [
        ...new Set(
          attendanceRecords.map((r) => r.workingCompany).filter(Boolean)
        ),
      ];

      console.log(
        `🔧 Loading configs for ${uniqueCompanies.length} companies: ${uniqueCompanies.join(", ") || "default only"
        }`
      );

      const configPromises = uniqueCompanies.map(async (companyName) => {
        try {
          const [currentRes, historyRes] = await Promise.all([
            apiFetch(
              `${BASE_URL}/api/company-config/${encodeURIComponent(
                clientName
              )}/${encodeURIComponent(companyName)}`,
              { method: "GET", headers: { "Accept-Language": language } }
            ),
            apiFetch(
              `${BASE_URL}/api/company-config/${encodeURIComponent(
                clientName
              )}/${encodeURIComponent(
                companyName
              )}/archive/history?limit=100`,
              { method: "GET", headers: { "Accept-Language": language } }
            ).catch(() => null),
          ]);

          const currentData = await currentRes.json();
          const historyData = historyRes
            ? await historyRes.json().catch(() => null)
            : null;

          if (currentData.success && currentData.data) {
            const rawHistory = Array.isArray(historyData?.data)
              ? historyData.data
              : [];
            const history = [...rawHistory].sort((a, b) => {
              const aDate = a.attendanceEffectiveDate
                ? new Date(a.attendanceEffectiveDate)
                : new Date(0);
              const bDate = b.attendanceEffectiveDate
                ? new Date(b.attendanceEffectiveDate)
                : new Date(0);
              return aDate - bDate;
            });
            console.log(
              `   Loaded config for ${companyName} (${history.length} archived versions)`
            );
            return { companyName, current: currentData.data, history };
          }
        } catch (error) {
          console.error(`❌ Error loading config for ${companyName}:`, error);
        }
        return null;
      });

      const results = await Promise.all(configPromises);

      const configMap = {};
      results.forEach((result) => {
        if (result) {
          configMap[result.companyName] = {
            current: result.current,
            history: result.history,
          };
        }
      });

      // ── default config (for records with no workingCompany) ────────────────
      if (attendanceRecords.some((r) => !r.workingCompany)) {
        try {
          const response = await apiFetch(
            `${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}`,
            { method: "GET", headers: { "Accept-Language": language } }
          );
          const respJson = await response.json();

          if (respJson.success && respJson.data) {
            let history = [];
            const defaultCompanyName = respJson.data.companyName;
            if (defaultCompanyName) {
              try {
                const historyRes = await apiFetch(
                  `${BASE_URL}/api/company-config/${encodeURIComponent(
                    clientName
                  )}/${encodeURIComponent(
                    defaultCompanyName
                  )}/archive/history?limit=100`,
                  { method: "GET", headers: { "Accept-Language": language } }
                ).catch(() => null);
                if (historyRes) {
                  const historyData = await historyRes
                    .json()
                    .catch(() => null);
                  const rawHistory = Array.isArray(historyData?.data)
                    ? historyData.data
                    : [];
                  history = [...rawHistory].sort((a, b) => {
                    const aDate = a.attendanceEffectiveDate
                      ? new Date(a.attendanceEffectiveDate)
                      : new Date(0);
                    const bDate = b.attendanceEffectiveDate
                      ? new Date(b.attendanceEffectiveDate)
                      : new Date(0);
                    return aDate - bDate;
                  });
                }
              } catch (e) {
                // archive history fetch failure is non-critical
              }
            }
            configMap["default"] = { current: respJson.data, history };
          }
        } catch (error) {
          console.error("❌ Error loading default config:", error);
        }
      }

      if (!configMap["default"]) {
        configMap["default"] = { current: FALLBACK_CONFIG, history: [] };
      }

      setCompanyConfigs(configMap);
      console.log(`✅ Loaded ${Object.keys(configMap).length} company configs`);

      return configMap;
    } catch (error) {
      console.error("❌ Error loading company configs:", error);
      const defaultConfig = { default: { current: FALLBACK_CONFIG, history: [] } };
      setCompanyConfigs(defaultConfig);
      return defaultConfig;
    }
  };

  // ── getConfigForEmployee ───────────────────────────────────────────────────
  // Thin wrapper — date-aware resolution is fully handled by getConfigForEmployeeOnDate.
  const getConfigForEmployee = (
    workingCompany,
    employeeType = null,
    recordDate = null
  ) => getConfigForEmployeeOnDate(workingCompany, employeeType, recordDate);

  // ── getConfigForEmployeeOnDate ─────────────────────────────────────────────
  // Returns the effective config object for a given company / employeeType / date.
  const getConfigForEmployeeOnDate = (
    workingCompany,
    employeeType = null,
    recordDate = null
  ) => {
    const bundle =
      (workingCompany && companyConfigs[workingCompany]) ||
      companyConfigs["default"] ||
      null;

    let baseConfig = bundle ? bundle.current : FALLBACK_CONFIG;

    if (recordDate && bundle) {
      const target = new Date(recordDate);
      target.setUTCHours(0, 0, 0, 0);

      const candidates = [];

      if (Array.isArray(bundle.history)) {
        for (const version of bundle.history) {
          if (!version.attendanceEffectiveDate) continue;
          const vDate = new Date(version.attendanceEffectiveDate);
          vDate.setUTCHours(0, 0, 0, 0);
          if (vDate <= target) {
            candidates.push({ config: version, effectiveDate: vDate });
          }
        }
      }

      if (bundle.current?.attendanceEffectiveDate) {
        const cDate = new Date(bundle.current.attendanceEffectiveDate);
        cDate.setUTCHours(0, 0, 0, 0);
        if (cDate <= target) {
          candidates.push({ config: bundle.current, effectiveDate: cDate });
        }
      }

      if (candidates.length > 0) {
        candidates.sort((a, b) => b.effectiveDate - a.effectiveDate);
        const best = candidates[0];
        baseConfig = best.config;
        if (best.config !== bundle.current) {
          console.log(
            `📅 Using archived config (effective ${best.effectiveDate.toISOString().split("T")[0]
            }) for date ${recordDate} / company ${workingCompany || "default"}`
          );
        }
      }
    }

    if (employeeType && baseConfig.attendanceRules) {
      let attendanceRules = baseConfig.attendanceRules;
      if (typeof attendanceRules === "string") {
        try {
          attendanceRules = JSON.parse(attendanceRules);
        } catch (e) {
          attendanceRules = {};
        }
      }
      if (attendanceRules[employeeType]) {
        const deptRules = attendanceRules[employeeType];
        console.log(
          `🎯 Using dept-specific config for: ${employeeType} in ${workingCompany || "default"
          }`
        );
        return { ...baseConfig, ...deptRules };
      }
    }

    return baseConfig;
  };

  return {
    clientConfig,
    configLoading,
    companyConfigs,
    loadClientConfig,
    loadAllCompanyConfigs,
    getConfigForEmployee,
    getConfigForEmployeeOnDate,
  };
};

export default useCompanyConfig;